import React, { useCallback, useEffect, useState } from 'react';

const EMPTY = { name: '' };

export function normalizeEnrollmentName(value) {
  return String(value || '').trim();
}

function statusLabel(status) {
  return status === 'connected' ? 'Connected' : 'Offline';
}

function connectorCommand(enrollment, cameraOnly = false) {
  return `export KATAFIT_RELAY_URL='${enrollment.relayUrl}'
export KATAFIT_SERVER_ID='${enrollment.serverId}'
read -r -s -p 'Paste the one-time token: ' KATAFIT_ENROLLMENT_TOKEN; printf '\\n'
export KATAFIT_ENROLLMENT_TOKEN
node bin/katafit-mcp-connector.js ${cameraOnly ? '--camera-device /dev/video0' : '-- <your-mcp-command> [arguments]'}
unset KATAFIT_ENROLLMENT_TOKEN`;
}

export default function CustomMcpSettings({ backendService, readOnly = false, establishedBy = null, scope }) {
  const [servers, setServers] = useState([]);
  const [ownership, setOwnership] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [enrollment, setEnrollment] = useState(null);

  const load = useCallback(async () => {
    const payload = await backendService.fetchMcpServers(scope);
    setServers(payload.servers || []);
    setOwnership(payload.ownership || null);
  }, [backendService, scope]);

  useEffect(() => {
    load().catch(loadError => setError(loadError.message || 'Could not load custom MCP servers.'));
  }, [load]);

  const refreshStatus = async () => {
    setBusy('refresh'); setMessage(null); setError(null);
    try {
      await load();
      setMessage('Connection status refreshed.');
    } catch (refreshError) {
      setError(refreshError.message || 'Could not refresh MCP connection status.');
    } finally { setBusy(null); }
  };

  const addServer = async event => {
    event.preventDefault();
    setBusy('add'); setMessage(null); setError(null);
    try {
      const next = await backendService.createMcpEnrollment(normalizeEnrollmentName(draft.name), scope);
      setServers(current => [...current, next.server]);
      setDraft(EMPTY);
      setEnrollment({ token: next.enrollment_token, relayUrl: next.relay_url, serverId: next.server.id });
      setMessage('Enrollment created. Copy the token now; it is shown only once.');
    } catch (saveError) { setError(saveError.message || 'Could not create MCP enrollment.'); } finally { setBusy(null); }
  };

  const removeServer = async server => {
    setBusy(`remove:${server.id}`); setMessage(null); setError(null);
    try {
      const next = await backendService.deleteMcpServer(server.id, scope);
      setServers(next.servers || []);
      setEnrollment(current => current?.serverId === server.id ? null : current);
      setMessage(`${server.name} removed.`);
    } catch (removeError) { setError(removeError.message || 'Could not remove MCP server.'); } finally { setBusy(null); }
  };


  const canEdit = !readOnly && ownership?.can_edit !== false;
  return (
    <section className="ai-custom-block" aria-label="Custom MCP servers">
      <h4 className="ai-custom-title">{scope === 'personal' ? 'Personal MCP servers' : scope === 'dojo' ? 'Dojo-app MCP servers' : 'Custom MCP servers'}</h4>
      <p className="ai-models-hint">
        Connect a private local MCP server through the outbound connector. No public MCP URL or inbound port is required.
        {scope === 'personal' && ' Registering a server makes its tools available to your personal Coach and automatically to your current Dojo Coach while you are a member. Tools may read, change or delete data, or communicate externally without per-call approval. Remove the server to stop future access; leaving a dojo ends that Dojo Coach’s access, and joining another grants its Coach access.'}
        {scope === 'dojo' && ' Chief-managed dojo app tools are available to the Dojo Coach.'}
        {readOnly && ` Established by ${establishedBy || 'your dojo'}; members can view but not manage these servers.`}
      </p>
      {canEdit && <div className="ai-mcp-guide">
        <h5>Set up a home MCP server</h5>
        <ol>
          <li><strong>Install the connector.</strong> On the home computer running your MCP server, install Node.js 22 or newer. Clone the <a href="https://github.com/stevefortier/katafit-mcp-connector" target="_blank" rel="noopener noreferrer">public Kata.fit MCP connector repository</a> with <code>git clone https://github.com/stevefortier/katafit-mcp-connector.git</code>, then run <code>npm ci</code> inside <code>katafit-mcp-connector</code>. No GitHub account is required; this connector is not published on npm.</li>
          <li><strong>Generate a token.</strong> Name one server below and register it. Copy the one-time enrollment token before closing this window. Registration alone does not connect tools. Each server currently needs its own registration and connector process; automatic home-device config sync is not yet tool-enabled.</li>
          <li><strong>Connect privately.</strong> On that computer, run the command shown after registration from the connector directory, replacing the placeholder with your local MCP server executable and arguments. The connection is outbound; keep the process running. Stop the connector and remove its registration to revoke future access.{scope === 'personal' && <> Webcam access is off by default. On Linux with FFmpeg, you can instead opt in to a selected webcam with <code>--camera-device /dev/video0</code> (repeat for more selected devices), by itself or before <code>-- &lt;your-mcp-command&gt;</code>. This exposes individual snapshots, not a video stream. A requested frame travels to Kata.fit and may be sent to your configured Coach model provider for vision analysis; review its data terms. Personal Coach can request a snapshot after your affirmative request, without another per-snapshot approval. Other tools on a personal registration may also be available to your current Dojo Coach; this webcam snapshot integration currently runs only in personal Coach.</>}</li>
          <li><strong>Verify.</strong> Tap Refresh status until it says Connected. Offline means no live connector; Connected indicates transport, not proof that the Coach has successfully called a tool.{scope === 'personal' && <> To request an enabled webcam from personal Coach, say “Take a webcam snapshot”; if several webcams are enabled, identify <code>camera_1</code>, <code>camera_2</code>, and so on in the order of your device flags.</>}</li>
        </ol>
      </div>}
      {error && <p className="ai-models-error" role="alert">{error}</p>}
      {message && <p className="ai-models-success" role="status">{message}</p>}
      {enrollment && <div className="ai-mcp-enrollment" role="region" aria-label="New MCP enrollment details">
        <p>One-time enrollment token (copy now; shown only in this session):</p>
        <code className="ai-mcp-token">{enrollment.token}</code>
        <p>From <code>katafit-mcp-connector</code> on your home computer, use Bash to run the following. The token is requested without putting it in the command or shell history:</p>
        <pre className="ai-mcp-command"><code>{connectorCommand(enrollment)}</code></pre>
        {scope === 'personal' && <><p>Replace <code>&lt;your-mcp-command&gt; [arguments]</code> with your actual local MCP launch command. Choose one launch command before using the one-time token: you cannot run both as separate processes with the same registration. Alternatively, if you explicitly want to expose a webcam, on Linux with FFmpeg use the camera-only command below after confirming <code>/dev/video0</code> is your intended capture device. Repeat <code>--camera-device</code> to expose additional cameras, or add <code>-- &lt;your-mcp-command&gt;</code> to combine camera and local server in one process.</p>
        <pre className="ai-mcp-command"><code>{connectorCommand(enrollment, true)}</code></pre></>}
        {scope !== 'personal' && <p>Replace <code>&lt;your-mcp-command&gt; [arguments]</code> with your actual local MCP launch command. Camera snapshots are currently supported only through a personal registration and personal Coach.</p>}
        <p>Do not paste a token into chat or commit it to a file. A connected process can reconnect after a network drop; after the process exits, this one-time token cannot enroll again. Remove the old registration and generate a new one before restarting.</p>
      </div>}
      <div className="ai-model-actions">
        <button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={refreshStatus}>{busy === 'refresh' ? 'Refreshing…' : 'Refresh status'}</button>
      </div>
      {servers.length > 0 ? (
        <ul className="ai-provider-list">
          {servers.map(server => (
            <li className="ai-provider-card" key={server.id}>
              <div className="ai-provider-card-head">
                <div className="ai-provider-identity"><strong className="ai-provider-name">{server.name}</strong><span>{statusLabel(server.connection_status)}</span></div>
                <span className={`ai-provider-badge ${server.connection_status === 'connected' ? 'ok' : ''}`}>{statusLabel(server.connection_status)}</span>
              </div>

              {server.last_seen && <p className="ai-models-hint">Last seen: {new Date(server.last_seen).toLocaleString()}</p>}
              <div className="ai-model-actions">
                {canEdit && <button type="button" className="ai-inline-btn danger" disabled={busy !== null} onClick={() => removeServer(server)}>{busy === `remove:${server.id}` ? 'Removing…' : 'Remove'}</button>}
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="ai-provider-empty">No private MCP servers configured.</p>}
      {/* Profile settings already live inside a form; nesting another form also submits Save Changes. */}
      {canEdit && <div className="ai-subform ai-subform-new">
        <span className="ai-subform-title">Register a private MCP server</span>
        {scope === 'personal' && <p className="ai-models-hint">By registering, you make this server available to your personal Coach and automatically to your current Dojo Coach while you belong to a dojo. Tools may read, change or delete data, or communicate externally without per-call approval. Remove the server to stop future calls.</p>}
        <div className="ai-field-grid">
          <label className="ai-field"><span>Name</span><input className="ai-provider-input" value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); if (busy === null) addServer(event); } }} placeholder="Training tools" /></label>
        </div>
        <div className="ai-subform-actions"><button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={addServer}>{busy === 'add' ? 'Registering…' : 'Register private MCP server'}</button></div>
      </div>}
    </section>
  );
}

// The enrollment token intentionally remains only in component state so it is never persisted or logged.
